// Moved from apps/frontend/src/containers/SegmentPageBuilder/SegmentWidgetPalette.tsx
// Pure UI — no app-specific dependencies.

import { memo, useCallback, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { X, BarChart2, TrendingUp, MessageSquare, Layers, Settings } from 'react-feather';

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect?: (type: string) => void;
  hideSections?: boolean;
};

type PaletteEntry = {
  type: string;
  label: string;
  description?: string;
  icon: React.ComponentType<{ size?: number; color?: string; style?: React.CSSProperties }>;
  action?: 'library-chart' | 'library-kpi' | 'library-quote' | 'custom';
};

const ENTRIES: PaletteEntry[] = [
  { type: 'library-chart', label: 'Chart', icon: BarChart2, description: 'From library', action: 'library-chart' },
  { type: 'library-kpi', label: 'KPI Card', icon: TrendingUp, description: 'From library', action: 'library-kpi' },
  { type: 'library-quote', label: 'Quote', icon: MessageSquare, description: 'From library', action: 'library-quote' },
  { type: 'section', label: 'Section', icon: Layers },
  { type: 'custom', label: 'Custom Widget', icon: Settings, description: 'Create from scratch', action: 'custom' },
];

type SegmentWidgetPaletteProps = Props & {
  onLibraryOpen?: (category: 'chart' | 'kpi' | 'quote') => void;
  onCustomOpen?: () => void;
};

export const SegmentWidgetPalette = memo(function SegmentWidgetPalette({
  open,
  onClose,
  onSelect,
  hideSections = false,
  onLibraryOpen,
  onCustomOpen,
}: SegmentWidgetPaletteProps): React.ReactElement {
  const [selected, setSelected] = useState<string | null>(null);

  const visibleEntries = hideSections ? ENTRIES.filter(e => e.type !== 'section') : ENTRIES;

  const handleClose = useCallback(() => {
    setSelected(null);
    onClose();
  }, [onClose]);

  const handleNext = useCallback(() => {
    if (!selected) return;
    const entry = ENTRIES.find(e => e.type === selected);
    if (!entry) return;

    if (entry.action === 'library-chart') { onLibraryOpen?.('chart'); setSelected(null); onClose(); return; }
    if (entry.action === 'library-kpi') { onLibraryOpen?.('kpi'); setSelected(null); onClose(); return; }
    if (entry.action === 'library-quote') { onLibraryOpen?.('quote'); setSelected(null); onClose(); return; }
    if (entry.action === 'custom') { onCustomOpen?.(); setSelected(null); onClose(); return; }

    if (onSelect) onSelect(selected);
    setSelected(null);
    onClose();
  }, [selected, onSelect, onClose, onLibraryOpen, onCustomOpen]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, px: 1 } }}>
      <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 2, position: 'relative' }}>
        <Typography variant="h6" fontWeight={600}>Add Widget</Typography>
        <IconButton onClick={handleClose} size="small" aria-label="Close" sx={{ position: 'absolute', top: 4, right: '-6px', color: 'text.disabled' }}>
          <X size={24} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 0, pb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
          {visibleEntries.map(entry => (
            <WidgetTile key={entry.type} selected={selected === entry.type} onClick={() => setSelected(entry.type)}>
              <entry.icon size={22} color="currentColor" style={{ marginBottom: 8 }} />
              <Typography variant="caption" fontWeight={500} noWrap sx={{ color: 'inherit' }}>{entry.label}</Typography>
              {entry.description && (
                <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.7, fontSize: '0.65rem', textAlign: 'center' }}>{entry.description}</Typography>
              )}
            </WidgetTile>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <CancelButton variant="outlined" fullWidth onClick={handleClose}>Cancel</CancelButton>
          <NextButton variant="contained" fullWidth disabled={!selected} onClick={handleNext}>Next</NextButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
});

const WidgetTile = styled(Box, { shouldForwardProp: prop => prop !== 'selected' })<{ selected: boolean }>(({ theme, selected }) => ({
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  padding: theme.spacing(3, 2),
  border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.grey[200]}`,
  borderRadius: theme.shape.borderRadius * 2, cursor: 'pointer',
  color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: selected ? `${theme.palette.primary.main}14` : theme.palette.background.paper,
  transition: 'border-color 0.15s, background-color 0.15s, color 0.15s', userSelect: 'none',
  ...(!selected && { '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: `${theme.palette.primary.main}08`, color: theme.palette.primary.main } }),
}));

const CancelButton = styled(Button)(({ theme }) => ({
  borderWidth: '2px', borderColor: theme.palette.grey[700], color: theme.palette.text.primary,
  textTransform: 'none', fontWeight: 500, backgroundColor: 'transparent',
  '&:hover': { borderWidth: '2px', borderColor: theme.palette.grey[900], backgroundColor: theme.palette.grey[200] },
}));

const NextButton = styled(Button)(() => ({ textTransform: 'none', fontWeight: 600 }));
